// Change #100462 - Super Admin > Enterprise Plans.
//
// Every Enterprise company with its plan: the users included (agreed seats),
// the tolerance (hard limit) and how many are in use, so CRMit can see who is
// over and change the numbers after a commercial conversation. Enterprise is
// chosen when CRMit creates the company, or switched in the Company Report.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Card, CardBody, CardHeader, Col, Input, Row, Table } from "reactstrap";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

import AppModal from "../../../../Components/Common/AppModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import {
  enterprisePlanAdminListApi,
  enterprisePlanAdminUpdateApi,
} from "../../../../helpers/fakebackend_helper";

const statusOf = (r) => {
  if (!r.PlanReady) return { color: "secondary", text: "No plan" };
  if (r.AtLimit) return { color: "danger", text: "At limit" };
  if (r.OverCap) return { color: "warning", text: "Over agreed" };
  return { color: "success", text: "Within plan" };
};

export default function EnterprisePlans() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notReady, setNotReady] = useState("");
  const [search, setSearch] = useState("");
  const [onlyOver, setOnlyOver] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await enterprisePlanAdminListApi();
      if (res?.status === 200) {
        setRows(res.enterpriseplan_list || []);
        setNotReady("");
      } else {
        setRows([]);
        setNotReady(res?.message || "Could not load the Enterprise plans");
      }
    } catch (e) {
      setNotReady("Could not load the Enterprise plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!onlyOver || r.OverCap || r.AtLimit) &&
        (!q ||
          String(r.BusinessName || "").toLowerCase().includes(q) ||
          String(r.PlanName || "").toLowerCase().includes(q))
    );
  }, [rows, search, onlyOver]);

  const overCount = rows.filter((r) => r.OverCap || r.AtLimit).length;

  const openEdit = (r) =>
    setEdit({
      Company_Id: r.Company_Id,
      BusinessName: r.BusinessName,
      PlanName: r.PlanName || "",
      MaxUsers: r.MaxUsers ?? "",
      MaxUsersTolerance: r.MaxUsersTolerance ?? "",
      Used: r.Used ?? 0,
    });

  const save = async () => {
    if (!edit) return;
    const max = Number(edit.MaxUsers);
    if (!(max > 0)) {
      toast.error("Enter how many users the plan includes");
      return;
    }
    if (edit.MaxUsersTolerance !== "" && Number(edit.MaxUsersTolerance) < max) {
      toast.error("The tolerance cannot be lower than the users included");
      return;
    }
    setSaving(true);
    try {
      const res = await enterprisePlanAdminUpdateApi([
        {
          Company_Id: edit.Company_Id,
          PlanName: edit.PlanName,
          MaxUsers: max,
          MaxUsersTolerance: edit.MaxUsersTolerance === "" ? null : Number(edit.MaxUsersTolerance),
        },
      ]);
      if (res?.status === 200) {
        toast.success("Plan updated");
        setEdit(null);
        load();
      } else {
        toast.error(res?.message || "Could not save the plan");
      }
    } catch (e) {
      toast.error("Could not save the plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="border-0">
          <Row className="g-2 align-items-end">
            <Col md={6}>
              <h5 className="mb-1">Enterprise Plans</h5>
              <p className="text-muted mb-0 fs-13">
                Companies CRMit bills directly. They add and remove their own users up to the tolerance and never
                buy licences. Choose Enterprise when creating a company, or switch an existing one in the{" "}
                <Link to="/company-report-list" className="link-primary fw-medium">
                  Company Report
                </Link>
                .
              </p>
            </Col>
            <Col md={3}>
              <div className="search-box">
                <Input
                  type="search"
                  className="form-control search"
                  placeholder="Search company or plan..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </Col>
            <Col md={3} className="text-md-end">
              <div className="form-check form-switch d-inline-flex align-items-center gap-2">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="ep-only-over"
                  checked={onlyOver}
                  onChange={(e) => setOnlyOver(e.target.checked)}
                />
                <label className="form-check-label fs-13" htmlFor="ep-only-over">
                  Only over agreed ({overCount})
                </label>
              </div>
            </Col>
          </Row>
        </CardHeader>
        <CardBody className="pt-0">
          {loading ? (
            <LoadingSpinner />
          ) : notReady ? (
            <div className="alert alert-warning mt-3 mb-0">{notReady}</div>
          ) : visible.length === 0 ? (
            <NoDataContent
              titleText={rows.length ? "Nothing matches" : "No Enterprise companies yet"}
              paragraphText={
                rows.length
                  ? "Try a different search."
                  : "Create a business and choose Enterprise under Billing, or switch an existing company with the Billing toggle in the Company Report."
              }
              useButton={false}
            />
          ) : (
            <div className="table-responsive table-card mt-3">
              <Table className="align-middle table-nowrap mb-0">
                <thead className="text-muted">
                  <tr>
                    <th>Company</th>
                    <th>Plan</th>
                    <th className="text-end">In use</th>
                    <th className="text-end">Included</th>
                    <th className="text-end">Allow up to</th>
                    <th className="text-end">Price</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => {
                    const st = statusOf(r);
                    return (
                      <tr key={r.Company_Id}>
                        <td>
                          <div className="fw-semibold">{r.BusinessName}</div>
                          <div className="text-muted fs-12">
                            #{r.Company_Id}
                            {String(r.FlagTest) === "1" ? " · test" : ""}
                          </div>
                        </td>
                        <td>{r.PlanName || <span className="text-muted">-</span>}</td>
                        <td className="text-end fw-bold">{r.PlanReady ? r.Used : "-"}</td>
                        <td className="text-end">{r.MaxUsers ?? "-"}</td>
                        <td className="text-end">{r.Limit ?? "-"}</td>
                        <td className="text-end">
                          {r.PriceLocalSubTotal != null ? (
                            <>
                              £{Number(r.PriceLocalSubTotal).toFixed(2)}
                              <div className="text-muted fs-12">{r.PricePeriod === "ANNUALLY" ? "per year" : "per month"}</div>
                            </>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <Badge color={st.color}>{st.text}</Badge>
                        </td>
                        <td className="text-end">
                          <div className="hstack gap-2 justify-content-end">
                            <button type="button" className="btn btn-sm btn-soft-primary" onClick={() => openEdit(r)}>
                              <i className="ri-pencil-line me-1"></i>Edit
                            </button>
                            {/* Change #100463 - base product, price and licences live on the company's Subscriptions tab */}
                            <Link to={`/company-details/${r.Company_Id}/Subscriptions`} className="btn btn-sm btn-soft-secondary">
                              <i className="ri-external-link-line me-1"></i>Subscriptions
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <AppModal
        open={Boolean(edit)}
        onClose={() => !saving && setEdit(null)}
        title="Enterprise plan"
        subtitle={edit?.BusinessName}
        icon="ri-shield-star-line"
        headerAccent="indigo"
        size="sm"
        onSubmit={save}
        submitLabel="Save"
        submitDisabled={saving}
        loading={saving}
        loadingText="Saving..."
      >
        {edit && (
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label mb-1">Plan name</label>
              <Input value={edit.PlanName} onChange={(e) => setEdit({ ...edit, PlanName: e.target.value })} />
            </div>
            <div className="col-6">
              <label className="form-label mb-1">
                Users included <span className="text-danger">*</span>
              </label>
              <Input
                type="number"
                min={1}
                value={edit.MaxUsers}
                onChange={(e) => setEdit({ ...edit, MaxUsers: e.target.value })}
              />
            </div>
            <div className="col-6">
              <label className="form-label mb-1">Allow up to (tolerance)</label>
              <Input
                type="number"
                min={1}
                placeholder="No headroom"
                value={edit.MaxUsersTolerance}
                onChange={(e) => setEdit({ ...edit, MaxUsersTolerance: e.target.value })}
              />
            </div>
            <div className="col-12 text-muted fs-13">
              {edit.Used} user{Number(edit.Used) === 1 ? "" : "s"} in use now. Lowering the limit below that does not
              remove anyone; it only stops new users being added.
            </div>
          </div>
        )}
      </AppModal>
    </>
  );
}
